import { isSameChatClientIdentity, normalizeChatClientId } from '../../chats/chatClientIdentity';
import { ALL_SENDER_IDS_VALUE } from '../../chats/inboxConstants';
import { ChatClient } from '../../types/chatClient';
import { AppState } from '../index';
import { SupportedChatChannel } from '../../chats/supportedChatChannel';
import { ChatChannelsAction } from '../../actions/chats';
import { ChatConversationId } from '../../types/chatMessage';
import { selectSelectedChatSenderId } from './senders';
import { PhoneNumberWithPlusSign } from '../../types/phoneNumber';

export interface ChatClientsState {
  selectedClientId?: string;
  /** Sender used for the active conversation when the inbox filter is "All Sender IDs". */
  selectedConversationSenderId?: string;
  entities: ChatClient[];
  loading: boolean;
  errorMessage?: string;
}

export const DEFAULT_CHAT_CLIENTS_STATE: ChatClientsState = {
  loading: false,
  entities: [],
};

const isSameClientIdentity = isSameChatClientIdentity;

export function chatClientsReducer(state: ChatClientsState, action: ChatChannelsAction): ChatClientsState {
  switch (action.type) {
    case 'chatChannels/clients/fetchPending': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'chatChannels/clients/fetchSuccess': {
      const { payload } = action;

      return {
        ...state,
        loading: false,
        entities: payload,
      };
    }
    case 'chatChannels/clients/fetchFailure': {
      const { payload } = action;

      return {
        ...state,
        loading: false,
        errorMessage: payload,
      };
    }

    case 'chatChannels/clients/addOne': {
      const { client } = action.payload;

      return {
        ...state,
        entities: [
          ...state.entities,
          client,
        ],
      };
    }
    case 'chatChannels/clients/upsertOne': {
      const { client } = action.payload;

      const updatedClients = [...state.entities];
      const existingIndex = updatedClients.findIndex(oldClient => isSameClientIdentity(oldClient, client));
      if (existingIndex !== -1) {
        updatedClients[existingIndex] = client;
      } else {
        updatedClients.push(client);
      }

      return {
        ...state,
        entities: updatedClients,
      };
    }
    case 'chatChannels/clients/replace': {
      const { clientIdToReplace, client } = action.payload;

      const updatedClients = [...state.entities]
        .filter(client => client.id !== clientIdToReplace);

      updatedClients.push(client);

      return {
        ...state,
        entities: updatedClients,
        ...(state.selectedClientId === clientIdToReplace && {
          selectedClientId: client.id,
        }),
      };
    }

    case 'chatChannels/clients/set': {
      const { clientId, conversationSenderId } = action.payload;

      return {
        ...state,
        selectedClientId: clientId || undefined,
        selectedConversationSenderId: clientId ? conversationSenderId : undefined,
      };
    }

    default: {
      return {
        ...state,
      };
    }
  }
}

const getChatClientState = (state: AppState, channel: SupportedChatChannel) => state.chatChannels[channel].clients;

const getIsLoadingClients = (state: ChatClientsState) => state.loading;
const getSelectedClientId = (state: ChatClientsState) => state.selectedClientId;
const getClientById = (state: ChatClientsState, clientId: string) => (
  state.entities.find(client => normalizeChatClientId(String(client.id)) === normalizeChatClientId(clientId))
);
const getClientBySenderIdAndPhoneNumber = (state: ChatClientsState, senderId: string, phoneNumber: PhoneNumberWithPlusSign) => (
  state.entities.find(client => String(client.senderId) === String(senderId) && client.number === phoneNumber)
);
const getClientByIdAndSenderId = (state: ChatClientsState, clientId: string, senderId: string) => (
  state.entities.find(
    client =>
      normalizeChatClientId(String(client.id)) === normalizeChatClientId(clientId) &&
      String(client.senderId) === String(senderId)
  )
);
const getAllClientsBySenderId = (state: ChatClientsState, senderId: string) => (
  state.entities.filter(client => String(client.senderId) === String(senderId))
);

export const selectIsLoadingChatClients = (state: AppState, channel: SupportedChatChannel) => {
  return getIsLoadingClients(getChatClientState(state, channel));
};
export const selectChatClientById = (
  state: AppState,
  channel: SupportedChatChannel,
  clientId: string,
  senderId?: string
) => {
  const chatClientState = getChatClientState(state, channel);
  return senderId
    ? getClientByIdAndSenderId(chatClientState, clientId, senderId)
    : getClientById(chatClientState, clientId);
};
export const selectChatClientBySenderIdAndPhoneNumber = (state: AppState, params: {
  readonly channel: SupportedChatChannel;
  readonly senderId: string;
  readonly phoneNumber: PhoneNumberWithPlusSign;
}) => {
  const { channel, senderId, phoneNumber } = params;
  return getClientBySenderIdAndPhoneNumber(getChatClientState(state, channel), senderId, phoneNumber);
};
export const selectChatClientsForSelectedSenderId = (state: AppState, channel: SupportedChatChannel) => {
  const selectedSenderId = selectSelectedChatSenderId(state, channel);
  const chatClientState = getChatClientState(state, channel);
  if (selectedSenderId === ALL_SENDER_IDS_VALUE) {
    return chatClientState.entities;
  }
  if (selectedSenderId) {
    return getAllClientsBySenderId(chatClientState, selectedSenderId);
  }
  // No sender is selected yet (e.g. the channel has no pricing / no senders
  // configured). Surface locally-created chats that have no senderId so the
  // user still lands in the conversation view after clicking "Select" in the
  // New Chat modal.
  return chatClientState.entities.filter((client) => !client.senderId);
};
export const selectChatClientByConversationId = (state: AppState, conversationId: ChatConversationId) => {
  const { channel, clientId, senderId } = conversationId;
  return getClientByIdAndSenderId(getChatClientState(state, channel), clientId, senderId);
};
export const selectSelectedChatClientId = (state: AppState, channel: SupportedChatChannel) => {
  return getSelectedClientId(getChatClientState(state, channel));
};
export const selectSelectedChatClient = (state: AppState, channel: SupportedChatChannel) => {
  const chatClientState = getChatClientState(state, channel);
  const selectedClientId = getSelectedClientId(chatClientState);
  const selectedSenderId = selectSelectedChatSenderId(state, channel);
  const conversationSenderId = chatClientState.selectedConversationSenderId;

  if (!selectedClientId) return undefined;

  if (selectedSenderId === ALL_SENDER_IDS_VALUE && conversationSenderId) {
    return getClientByIdAndSenderId(chatClientState, selectedClientId, conversationSenderId);
  }

  return selectedSenderId && selectedSenderId !== ALL_SENDER_IDS_VALUE
    ? getClientByIdAndSenderId(chatClientState, selectedClientId, selectedSenderId)
    : getClientById(chatClientState, selectedClientId);
};
